/**
 * HOOK — Zarządzanie autentykacją - BULLETPROOF VERSION
 * Konwencja: Sikorski (H = si naturalne, B = si bemol)
 * 
 * Zabezpieczenia:
 * - Hard timeout 8s (nigdy nie zostaje isLoading=true)
 * - Safe session timeout (5s dla getSession)
 * - Retry mechanism
 * - Wszystkie ścieżki kodu kończą się setIsLoading(false)
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { 
  supabase, 
  isSupabaseConfigured, 
  getSessionWithTimeout, 
  nukeSessionAndReload 
} from '@/lib/supabase/client'

import type { UserRole, LoginCredentials, RegisterData } from '../types'

// Session z Supabase - bez user_profile
interface SupabaseSession {
  user: {
    id: string
    email?: string
    user_metadata?: {
      display_name?: string
      role?: UserRole
    }
  } | null
}

// Rola pochodzi z user_profiles.role (DB authoritative). NIE z user_metadata,
// bo to user-controlled pole (vektory eskalacji privilege — patrz migracja
// 008 + 009). Hook ma cache na pojedynczy fetch; pełen refetch dopiero przy
// kolejnym logowaniu / odświeżeniu strony.
async function fetchProfileRole(userId: string): Promise<UserRole | undefined> {
  if (!supabase) return undefined
  try {
    const { data } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', userId)
      .single()
    return data?.role as UserRole | undefined
  } catch {
    return undefined
  }
}

export function useAuth() {
  // Stan - user to tylko dane z auth.users, profil jest osobno w useProfile
  const [user, setUser] = useState<{ id: string; email?: string; displayName?: string; role?: UserRole } | null>(null)
  const [session, setSession] = useState<SupabaseSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  
  // Refs dla cleanup
  const mountedRef = useRef(true)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Czyścimy timeout przy unmount
  const clearHardTimeout = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }
  }, [])

  // HARD TIMEOUT - bezwarunkowo wyłącza isLoading po 8s
  // UWAGA: Bez user w deps - inaczej infinite loop!
  const startHardTimeout = useCallback(() => {
    clearHardTimeout()
    timeoutIdRef.current = setTimeout(() => {
      if (mountedRef.current) {
        console.warn('[useAuth] Hard timeout 8s - forcing isLoading=false')
        setIsLoading(false)
        // Nie ustawiamy error tutaj - po prostu wyłączamy ładowanie
      }
    }, 8000)
  }, [clearHardTimeout])

  // Retry - inkrementuje nonce który jest w deps useEffect
  const retry = useCallback(() => {
    setError(null)
    setIsLoading(true)
    setRetryNonce(n => n + 1)
  }, [])

  /**
   * Inicjalizacja - BEZPIECZNA z timeoutami i error handling
   */
  useEffect(() => {
    // Jeśli Supabase nie skonfigurowany - od razu błąd
    if (!isSupabaseConfigured) {
      setError('Błąd konfiguracji aplikacji (brak połączenia z bazą)')
      setIsLoading(false)
      return
    }

    mountedRef.current = true
    setIsLoading(true)
    setError(null)
    
    // HARD TIMEOUT - zawsze kończy isLoading po 8s
    startHardTimeout()

    const initAuth = async () => {
      try {
        // Timeout 5s dla getSession (zabezpieczenie przed zawieszeniem)
        const { data, error: sessionError } = await getSessionWithTimeout(5000)

        // Jeśli timeout lub błąd - traktujemy jako niezalogowany (nie pokazujemy błędu)
        if (sessionError?.message?.includes('timeout')) {
          console.warn('[useAuth] Session timeout - treating as logged out')
          if (mountedRef.current) {
            setUser(null)
            setSession(null)
          }
        } else if (sessionError) {
          console.error('[useAuth] Session error:', sessionError)
          if (mountedRef.current) {
            setUser(null)
            setSession(null)
          }
        } else if (data.session?.user && mountedRef.current) {
          // Zalogowany — ustawiamy usera, role pobieramy z user_profiles
          const userData = data.session.user
          const metadata = (userData as { user_metadata?: { display_name?: string; role?: UserRole } }).user_metadata
          const role = await fetchProfileRole(userData.id)
          if (mountedRef.current) {
            setUser({
              id: userData.id,
              email: userData.email,
              displayName: metadata?.display_name,
              role,
            })
            setSession({ user: { ...userData, user_metadata: metadata } })
          }
        }
      } catch (err) {
        console.error('[useAuth] Init error:', err)
        if (mountedRef.current) {
          setUser(null)
          setSession(null)
        }
      } finally {
        // ZAWSZE wyłączamy isLoading - każda ścieżka kodu
        if (mountedRef.current) {
          setIsLoading(false)
        }
        clearHardTimeout()
      }
    }

    initAuth()

    // Subskrypcja zmian auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!mountedRef.current) return
        
        // WAŻNE: Każdy event musi ustawić isLoading=false
        // Inaczej niezalogowany user utknie na "Ładowanie..." do hard timeout
        
        if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && currentSession?.user) {
          // Zalogowany — ustawiamy bez roli, potem fire-and-forget fetch z DB
          const userData = currentSession.user
          const metadata = (userData as { user_metadata?: { display_name?: string; role?: UserRole } }).user_metadata
          setUser({
            id: userData.id,
            email: userData.email,
            displayName: metadata?.display_name,
            role: undefined,
          })
          setSession({ user: userData })
          setIsLoading(false)
          fetchProfileRole(userData.id).then((role) => {
            if (!mountedRef.current) return
            setUser((u) => (u && u.id === userData.id ? { ...u, role } : u))
          })
        } else if (event === 'SIGNED_OUT') {
          // Wylogowany
          setUser(null)
          setSession(null)
          setIsLoading(false)
        } else if (event === 'TOKEN_REFRESHED' && !currentSession?.user) {
          // Refresh się nie powiódł - wyloguj cicho
          setUser(null)
          setSession(null)
          setIsLoading(false)
        } else if (event === 'INITIAL_SESSION' && !currentSession?.user) {
          // INITIAL_SESSION bez usera = niezalogowany, zakończ ładowanie
          setUser(null)
          setSession(null)
          setIsLoading(false)
        }
        // Wszystkie inne eventy też muszą kończyć ładowanie
        // ale nie zmieniają stanu usera
      }
    )

    return () => {
      mountedRef.current = false
      clearHardTimeout()
      subscription.unsubscribe()
    }
  }, [retryNonce, startHardTimeout, clearHardTimeout])

  /**
   * Logowanie - z timeout safeguard
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    if (!isSupabaseConfigured) {
      setError('Błąd konfiguracji aplikacji')
      return false
    }

    setIsLoading(true)
    setError(null)
    startHardTimeout()

    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      })

      if (loginError) {
        throw new Error(loginError.message)
      }

      if (data.user) {
        const userData = data.user
        const metadata = (userData as { user_metadata?: { display_name?: string; role?: UserRole } }).user_metadata
        const role = await fetchProfileRole(userData.id)
        setUser({
          id: userData.id,
          email: userData.email,
          displayName: metadata?.display_name,
          role,
        })
        setSession({ user: { ...userData, user_metadata: metadata } })
        return true
      }

      return false
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Błąd logowania'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
      clearHardTimeout()
    }
  }, [startHardTimeout, clearHardTimeout])

  /**
   * Rejestracja - z timeout safeguard
   */
  const register = useCallback(async (data: RegisterData): Promise<boolean> => {
    if (!isSupabaseConfigured) {
      setError('Błąd konfiguracji aplikacji')
      return false
    }

    setIsLoading(true)
    setError(null)
    startHardTimeout()

    try {
      // UWAGA: nie wysyłamy `role` w user_metadata — i tak nie ma już efektu
      // (trigger handle_new_user wymusza role='student'), ale brak parametru
      // upraszcza powierzchnię ataku i czyści payload signupu.
      const { data: authData, error: registerError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            display_name: data.displayName,
          },
        },
      })

      if (registerError) {
        throw new Error(registerError.message)
      }

      if (authData.user) {
        const userData = authData.user
        const metadata = (userData as { user_metadata?: { display_name?: string; role?: UserRole } }).user_metadata

        // Jeśli podano kod zaproszenia — zrealizuj go (klasa lub nauczyciel)
        if (data.inviteCode) {
          await supabase.rpc('resolve_invite_code', { invite_code: data.inviteCode.toUpperCase() })
        }

        // Świeży signup zawsze ma role='student' (trigger 008 to wymusza).
        // Czytamy z DB dla pewności (cover'uje race condition gdyby trigger
        // jeszcze nie wstawił wiersza, zwróci undefined → fallback).
        const role = (await fetchProfileRole(userData.id)) ?? ('student' as UserRole)
        setUser({
          id: userData.id,
          email: userData.email,
          displayName: metadata?.display_name,
          role,
        })
        setSession({ user: { ...userData, user_metadata: metadata } })
        return true
      }

      return false
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Błąd rejestracji'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
      clearHardTimeout()
    }
  }, [startHardTimeout, clearHardTimeout])

  /**
   * Wylogowanie - z fallbackiem do nukeSession
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true)

    try {
      if (isSupabaseConfigured) {
        await supabase.auth.signOut()
      }
      setUser(null)
      setSession(null)
    } catch (err) {
      console.error('[useAuth] Logout error, nuking session:', err)
      // Jeśli normalne wylogowanie się nie udało - atomowe czyszczenie
      await nukeSessionAndReload()
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Dołączanie do klasy przez kod zaproszenia
   */
  const joinClassByCode = useCallback(async (code: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('join_class_by_code', {
        invitation_code: code.toUpperCase(),
      })

      if (error) throw error

      return {
        success: data.success,
        message: data.message,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Błąd dołączania do klasy'
      return { success: false, message }
    }
  }, [])

  /**
   * Sprawdź czy użytkownik ma daną rolę
   */
  const hasRole = useCallback((role: UserRole | UserRole[]): boolean => {
    if (!user || !user.role) return false

    if (Array.isArray(role)) {
      return role.includes(user.role)
    }

    return user.role === role
  }, [user])

  return {
    user,
    session,
    isLoading,
    error,
    isAuthenticated: !!user,
    isTeacher: user?.role === 'teacher',
    isStudent: user?.role === 'student',
    isAdmin: user?.role === 'admin',
    isConfigured: isSupabaseConfigured,
    login,
    register,
    logout,
    retry,
    joinClassByCode,
    hasRole,
    clearError: () => setError(null),
    nukeSessionAndReload,
  }
}
